<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, email, minLength, maxLength } from '@vuelidate/validators';
import { type TCountryCode, countries } from 'countries-list';
import InputOtp from 'primevue/inputotp';
import { useOrganizationsRepo } from '~/src/entities/organization';
import { type ApiPaths, type JsonRequestBody, type JsonResponse, apiFetch } from '~/src/shared/api';
import { supportedCountries } from '~/src/shared/model/countries';
import { sendVerificationCode } from '~/src/entities/verification-code';
import { useCurrentOrganizationId } from '~/src/shared/lib/current-user';

const user = useLogtoUser();
const toast = useToast();
const activeStep = ref(0);

const showInputOtpDialog = ref(false);
const otp = ref('');

const showPasswordInput = ref(false);
const password = ref('');

const emailAddress = ref(user?.email || '');
watch(emailAddress, () => {
  if (showPasswordInput.value) {
    showPasswordInput.value = false;
  }
});

const organizationInfo = reactive({
  name: '',
  countryCode: 'US' as TCountryCode,
});

const emailRules = computed(() => ({
  emailAddress: {
    required: helpers.withMessage('Oops! It looks like you forgot to enter your email address.', required),
    email: helpers.withMessage(`Hmm, that doesn't look like a valid email address. Could you check it again?`, email),
  },
}));

const passwordRules = computed(() => ({
  password: {
    required: helpers.withMessage('Oops! It seems you forgot to enter your password.', required),
    minLength: helpers.withMessage('Your password needs to be at least 6 characters long. Can you make it a bit longer?', minLength(6)),
  },
}));

const oranizationRules = computed(() => ({
  name: {
    required: helpers.withMessage('Oops! It looks like you forgot to enter the name of your company', required),
    minLength: helpers.withMessage('Seems too long :( Please shorten it to 128 characters or less.', maxLength(128)),
  },
  countryCode: {
    required,
  },
}));

const emailValidation = useVuelidate(emailRules, { emailAddress });
const passwordValidation = useVuelidate(passwordRules, { password });

const organizationValidation = useVuelidate(oranizationRules, organizationInfo);

const isLoading = ref(false);
async function checkEmail() {
  const validEmail = await emailValidation.value.$validate();
  if (!validEmail) {
    return;
  }
  isLoading.value = true;
  try {
    const userExists = await apiFetch<JsonResponse<ApiPaths['/v1']['/auth/check-account-with-email-exists']['post'], 200>>('/v1/auth/check-account-with-email-exists', {
      method: 'POST',
      query: {
        email: emailAddress.value,
      },
    });
    if (userExists) {
      navigateTo('/sign-in', { external: true });
    } else {
      showPasswordInput.value = true;
      // showInputOtpDialog.value = true;
      // sendVerificationCode({ email: email.value });
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred. Please try again.',
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

async function handleButtonClick() {
  if (activeStep.value === 0) {
    if (showPasswordInput.value) {
      const validPassword = await passwordValidation.value.$validate();
      if (!validPassword) {
        return;
      }
      activeStep.value = 1;
    } else if (user) {
      activeStep.value = 1;
    } else {
      checkEmail();
    }
  } else if (activeStep.value === 1 && !user) {
    const validOrganization = await organizationValidation.value.$validate();
    if (!validOrganization) {
      return;
    }
    isLoading.value = true;
    try {
      await sendVerificationCode({
        email: emailAddress.value,
      });
      showInputOtpDialog.value = true;
    } catch {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not send verification code. Please check your email and try again.',
      });
    } finally {
      isLoading.value = false;
    }
  } else if (activeStep.value === 1 && user) {
    createOrganization();
  }
}

async function registerAndCreateOrganization() {
  showInputOtpDialog.value = false;
  const validOrganization = await organizationValidation.value.$validate();
  if (!validOrganization) {
    return;
  }
  isLoading.value = true;
  try {
    const body: JsonRequestBody<ApiPaths['/v1']['/flows/register-and-create-organization']['post']> = {
      verificationCode: otp.value,
      user: {
        primary_email: emailAddress.value,
        first_name: '',
        last_name: '',
        password: password.value,
      },
      organization: {
        name: organizationInfo.name,
        timezone_id: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        offerings_mode: false, // TODO: Remove this when offerings_mode is removed from the API
        country_code: organizationInfo.countryCode,
        currency_code: countries[organizationInfo.countryCode].currency[0],
      },
    };
    await apiFetch('/v1/flows/register-and-create-organization', {
      method: 'POST',
      body,
    });
    navigateTo('/sign-in', { external: true });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred. Please try again.',
    });
  } finally {
    isLoading.value = false;
  }
}

const currentOrgID = await useCurrentOrganizationId();
const orgsRepo = useOrganizationsRepo();
async function createOrganization() {
  const validOrganization = await organizationValidation.value.$validate();
  if (!validOrganization) {
    return;
  }
  isLoading.value = true;
  try {
    const organization = await orgsRepo.create({
      name: organizationInfo.name,
      timezone_id: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      offerings_mode: false, // TODO: Remove this when offerings_mode is removed from the API
      country_code: organizationInfo.countryCode,
      currency_code: countries[organizationInfo.countryCode].currency[0],
    });

    currentOrgID.value = organization.id;

    // external is required to retrieve a new token
    navigateTo('/admin', { external: true });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create organization. Please try again.',
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

async function submitOtp() {
  if (user) {
    createOrganization();
  } else {
    registerAndCreateOrganization();
  }
}
</script>

<template>
  <div class="container py-20 max-w-lg">
    <PToast />

    <div class="space-y-10">
      <h1 class="text-5xl text-center">
        Connect organization
      </h1>

      <PSteps v-model:activeStep="activeStep" :readonly="activeStep === 0" :model="[
        {
          label: 'Owner Info',
        },
        {
          label: 'Organization Info',
        },
      ]" />

      <div class="flex flex-col max-w-xs mx-auto">
        <template v-if="activeStep === 0">
          <template v-if="!user">
            <DfInputWrapper label="Root user email" help-text="You can change this later" :errors="emailValidation.emailAddress.$errors">
              <PInputText v-model="emailAddress" placeholder="email@example.com" @keyup.enter="handleButtonClick" />
            </DfInputWrapper>
          </template>

          <template v-else>
            <p class="leading-loose">
              Signed in as <b>{{ user.email }}</b>
              <br>
              Not you? <NuxtLink to="/sign-out" external class="text-indigo-700 underline">
                Sign out
              </NuxtLink>
            </p>
          </template>

          <DfInputWrapper v-if="showPasswordInput" label="Create a password" :errors="passwordValidation.password.$errors" class="mt-6">
            <PPassword v-model="password" placeholder="Password" input-class="w-full" @keyup.enter="handleButtonClick" />
          </DfInputWrapper>
          <PButton label="Continue" :loading="isLoading" required class="mt-6" @click="handleButtonClick" />
        </template>

        <template v-else-if="activeStep === 1">
          <div class="flex flex-col space-y-6">
            <DfInputWrapper label="Company name" :errors="organizationValidation.name.$errors">
              <PInputText v-model="organizationInfo.name" placeholder="Company name" />
            </DfInputWrapper>

            <DfInputWrapper label="Country" help-text="This is required for tax calculation">
              <DfCountrySelect v-model="organizationInfo.countryCode" :options="supportedCountries" />
            </DfInputWrapper>
          </div>

          <PButton label="Create organization" size="large" :loading="isLoading" class="mt-6" @click="handleButtonClick" />

          <PDialog v-model:visible="showInputOtpDialog" modal header="Please confirm your email" :draggable="false">
            <div class="flex flex-col items-center space-y-4">
              <p>You will receive an email with one-time code now. Please enter it below.</p>
              <InputOtp v-model="otp" :length="6" />
              <PButton label="Confirm" size="large" @click="submitOtp" />
            </div>
          </PDialog>
        </template>
      </div>
    </div>

    <PCard v-if="!user" class="text-center mt-20">
      <template #title>
        Already registered?
      </template>
      <template #content>
        <p>You already got your account and want to log in?</p>
        <NuxtLink to="/sign-in" external>
          <PButton icon="pi pi-arrow-right" icon-pos="right" text label="Sign in" class="mt-4" />
        </NuxtLink>
      </template>
    </PCard>
  </div>
</template>
